import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../variables/api/axiosInstance";
import {
  Button,
  DatePicker,
  Input,
  Modal,
  Select,
  Table,
  Typography,
  Popconfirm,
} from "antd";
import SinglePageLayout from "../../../../layout/SinglePageLayout";
import TextArea from "antd/es/input/TextArea";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { frenchDate } from "../../../../variables/functions";
import ActionDropdown from "../../../production/components/ActionDropdown";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

const CleDeRepartition = () => {
  const [reload, setReload] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [branches, setBranches] = useState([]);
  const [branche, setBranche] = useState("");
  const [organismes, setOrganismes] = useState([]);
  const [organisme, setOrganisme] = useState("");
  const [cle, setCle] = useState(0);

  useEffect(() => {
    axiosInstance.get(`/api/branche`).then((res) => setBranches(res.data));
    axiosInstance
      .get(`/api/organisme/assurance`)
      .then((res) => setOrganismes(res.data));
    axiosInstance
      .get(`/api/cle-repartition`)
      .then((res) => setLignes(res.data.reverse()));
  }, [reload]);

  const resetForm = () => {
    setDateDebut(null);
    setDateFin(null);
    setCode("");
    setDescription("");
    setBranche("");
    setOrganisme("");
    setCle(0);
    setSelectedRecord(null);
    setEditMode(false);
  };

  const onSubmitCleRepartition = () => {
    const data = {
      dateDebut: dateDebut?.toISOString(),
      dateFin: dateFin?.toISOString(),
      code,
      commentaire: description,
      branche,
      compagnie: organisme,
      cle: parseFloat(cle),
    };

    const isDuplicate =
      !editMode &&
      lignes.some(
        (ligne) =>
          ligne.code === code &&
          ligne.branche?.codeBranche === branche &&
          ligne.compagnie?.code === organisme &&
          ligne.cle === parseFloat(cle)
      );

    if (isDuplicate) {
      toast.error("Cette clé de répartition existe déjà.");
      return;
    }

    const request = editMode
      ? axiosInstance.put(`/api/cle-repartition`, {
        ...data,
        cleRepartitionId: selectedRecord?.cleRepartitionId,
      })
      : axiosInstance.post(`/api/cle-repartition`, data);

    request
      .then(() => {
        toast.success(`Clé ${editMode ? "modifiée" : "ajoutée"} avec succès.`);
        setOrderModalOpen(false);
        resetForm();
        setReload(!reload);
      })
      .catch(() => toast.error("Erreur lors de l'enregistrement."));
  };

  const handleEdit = (record) => {
    setEditMode(true);
    setSelectedRecord(record);
    setDateDebut(record.dateDebut ? dayjs(record.dateDebut) : null);
    setDateFin(record.dateFin ? dayjs(record.dateFin) : null);
    setCode(record.code || "");
    setDescription(record.commentaire || "");
    setBranche(record.branche?.codeBranche || "");
    setOrganisme(record.compagnie?.code || "");
    setCle(record.cle || 0);
    setOrderModalOpen(true);
  };

  const handleDelete = (cleRepartitionId) => {
    axiosInstance
      .delete(`/api/cle-repartition/${cleRepartitionId}`)
      .then(() => {
        toast.success("Clé supprimée avec succès.");
        setReload(!reload);
      })
      .catch(() => toast.error("Erreur lors de la suppression."));
  };

  const columns = [
    {
      title: "Période",
      key: "periode",
      render: (_, record) =>
        `${frenchDate(record.dateDebut)} ${record.dateFin ? ` au ${frenchDate(record.dateFin)}` : ""
        }`,
    },
    {
      title: "Compagnie",
      key: "compagnie",
      render: (_, record) => record.compagnie?.raisonSociale || "N/A",
    },
    {
      title: "Branche",
      key: "branche",
      render: (_, record) => record.branche?.nom || "N/A",
    },
    {
      title: "Taux (%)",
      key: "taux",
      render: (_, record) => record.cle || "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <EditOutlined />,
              onClick: () => handleEdit(record),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () =>
                Modal.confirm({
                  title: "Confirmer la suppression ?",
                  okText: "Oui",
                  cancelText: "Non",
                  okType: "danger",
                  onOk: () => handleDelete(record.cleRepartitionId),
                }),
            },
          ]}
        />
      ),
    },
  ];

  const disabledDate = (current) => current && current < dateDebut;

  return (
    <SinglePageLayout currentMenu="AUTRES_PARAMETRES">
      <Typography.Title style={{ fontSize: "26px", color: "#1a1a1a" }}>
        Définition de la clé de répartition
      </Typography.Title>
      <Button
        icon={<i className="bx bx-plus"></i>}
        type="primary"
        onClick={() => setOrderModalOpen(true)}
      >
        Définir une nouvelle clé
      </Button>
      <Table
        dataSource={lignes}
        columns={columns}
        rowKey="cleRepartitionId" // ✅ ID correct
        pagination={{ pageSize: 10 }}
      />
      <Modal
        title={
          editMode
            ? "Modifier la clé de répartition"
            : "Nouvelle clé de répartition"
        }
        open={orderModalOpen}
        onOk={onSubmitCleRepartition}
        onCancel={() => {
          setOrderModalOpen(false);
          resetForm();
        }}
        width={700}
      >
        <div className="row">
          <div className="col-6">
            <label style={{ color: "#1a1a1a" }}>Date début *</label>
            <DatePicker
              value={dateDebut}
              onChange={setDateDebut}
              style={{ width: "100%" }}
            />
          </div>
          <div className="col-6">
            <label style={{ color: "#1a1a1a" }}>Date fin</label>
            <DatePicker
              value={dateFin}
              onChange={setDateFin}
              style={{ width: "100%" }}
              disabledDate={disabledDate}
            />
          </div>
          <div className="col-6">
            <label style={{ color: "#1a1a1a" }}>Clé (%) *</label>
            <Input
              value={cle}
              onChange={(e) => setCle(e.target.value)}
              type="number"
              min={0}
              placeholder="Entrez la clé de répartition"
            />
          </div>
          <div className="col-6">
            <label style={{ color: "#1a1a1a" }}>Branche *</label>
            <Select
              value={branche}
              onChange={setBranche}
              style={{ width: "100%" }}
            >
              {branches?.map((br) => (
                <Select.Option
                  key={br.codeBranche}
                  value={br.codeBranche}
                >
                  {br.nom}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="col-6">
            <label style={{ color: "#1a1a1a" }}>Organisme *</label>
            <Select
              value={organisme}
              onChange={setOrganisme}
              style={{ width: "100%" }}
            >
              {organismes?.map((org) => (
                <Select.Option key={org.code} value={org.code}>
                  {org.raisonSociale}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="col-6">
            <label style={{ color: "#1a1a1a" }}>Code</label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              type="text"
              placeholder="Entrez le code"
            />
          </div>
          <div className="col-12">
            <label style={{ color: "#1a1a1a" }}>Description</label>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </SinglePageLayout>
  );
};

export default CleDeRepartition;
